import BaseModel from './BaseModel';
import { v4 } from 'uuid';
import { EmailLayoutWidget } from './EmailLayoutWidgets';
import { Utils } from '../services';
import {
    CustomTextWidget,
    EntryQrWidget,
    ImageWidget,
    LibraryImageWidget
} from './EmailLayoutWidgets';
import * as _ from 'es-toolkit';


/**
 * Escape RegExp in short codes
 * Use RegExp.escape natif if available, fallback otherwise
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/escape
 */
function escapeShortcode(string) {
    if (typeof RegExp.escape === 'function') {
        return RegExp.escape(string);
    }
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}


/** Class representing a PDF layout. */
class EmailLayout extends BaseModel {
    static LAYOUT_TYPE_TICKET = 'ticket';
    static LAYOUT_TYPE_ORDER = 'order';
    static LAYOUT_TYPE_GIFT = 'gift';
    static LAYOUT_TYPE_REFUND = 'refund';
    static LAYOUT_TYPE_BOOKING = 'booking';
    static LAYOUT_TYPE_DISTRIBUTOR_REPORT = 'distributor-report';
    static LAYOUT_TYPE_APPLICATION = 'application';
    static LAYOUT_TYPE_ACCREDITATION = 'accreditation';
    static LAYOUT_TYPE_CAMPAIGN = 'campaign';

    /* Widget types */
    static TEXT          = 'text';
    static IMAGE         = 'image';
    static CUSTOM        = 'custom';
    static LIBRARY_IMAGE = 'library';
    static ENTRY_QRCODE  = 'entry_qrcode';

    /* Options types */
    static OPTION_COLOR             = 'color';
    static OPTION_LINKS_COLOR       = 'linksColor';
    static OPTION_BACKGROUND_COLOR  = 'background-color';
    static OPTION_TEXT              = 'text';
    static OPTION_TEXT_ALIGNMENT    = 'text-alignment';
    static OPTION_TEXT_DECORATION   = 'text-decoration';
    static OPTION_FONT_FAMILY       = 'font-family';
    static OPTION_FONT_SIZE         = 'font-size';
    static OPTION_LINE_HEIGHT       = 'line-height';
    static OPTION_ALIGNMENT         = 'alignment';
    static OPTION_PADDING           = 'padding';
    static OPTION_OBJECT_FIT        = 'object-fit';
    static OPTION_DATE_FORMAT       = 'date-format';
    static OPTION_IMAGE             = 'image';
    static OPTION_IMAGE_TYPE        = 'image-type';
    static OPTION_BORDER_STYLE      = 'border-style';
    static OPTION_BORDER_WIDTH      = 'border-width';
    static OPTION_BORDER_COLOR      = 'border-color';
    static OPTION_BORDER_RADIUS     = 'border-radius';
    static OPTION_LINK              = 'link';
    static OPTION_TEXT_TRANSFORM    = 'text-transform';

    static CUSTOM_TEXT_SHORTCODE    = 'custom.text';

    static imageTypes = () => {
        return Utils.image_types('setting');
    };

    static types = () => [
        EmailLayout.LAYOUT_TYPE_TICKET,
        EmailLayout.LAYOUT_TYPE_ORDER,
        EmailLayout.LAYOUT_TYPE_GIFT,
        EmailLayout.LAYOUT_TYPE_REFUND,
        EmailLayout.LAYOUT_TYPE_BOOKING,
        EmailLayout.LAYOUT_TYPE_DISTRIBUTOR_REPORT,
        EmailLayout.LAYOUT_TYPE_APPLICATION,
        EmailLayout.LAYOUT_TYPE_ACCREDITATION,
        EmailLayout.LAYOUT_TYPE_CAMPAIGN,
    ];

    static filter_by_types(layouts, types) {
        if (!Array.isArray(layouts))
            return [];

        if (!Array.isArray(types) || types.length === 0)
            return layouts;

        return layouts.filter(layout => types.includes(layout?.type));
    }

    /**
     * Layout types offered when composing a message from a list.
     * Campaign is always available; ticket / application layouts are added
     * when recipients carry the matching context.
     */
    static types_for_message_recipients(recipients) {
        const types = [];
        const list = Array.isArray(recipients) ? recipients : [];

        if (list.some(recipient => EmailLayout.recipient_has_ticket_context(recipient)))
            types.push(EmailLayout.LAYOUT_TYPE_TICKET);

        if (list.some(recipient => recipient?.context?.application))
            types.push(EmailLayout.LAYOUT_TYPE_APPLICATION);

        types.push(EmailLayout.LAYOUT_TYPE_CAMPAIGN);

        return types;
    }

    /** True when the mailer can resolve a ticket for this recipient. */
    static recipient_has_ticket_context(recipient) {
        const context = recipient?.context;
        if (!context || typeof context !== 'object')
            return false;
        if (context.ticket_id != null && String(context.ticket_id).trim() !== '')
            return true;
        const ticket_id = context.ticket?._id ?? context.ticket?.id;
        return ticket_id != null && String(ticket_id).trim() !== '';
    }

    static objectFitValues = () => {
        return [
            'none',
            /*'fill', fill is not supported by PDFKit */
            'cover',
            'contain'
        ];
    };

    static borderStyleValues = () => {
        return [
            'solid',
            'dashed',
            /*'dotted', dotted is not supported by PDFKit */
        ];
    };

    static textDecorationValues = () => {
        return [
            'none',
            'underline',
            'line-through',
        ];
    };

    static textTransformValues = () => {
        return [
            'none',
            'capitalize',
            'uppercase',
            'lowercase'
        ];
    };

    static dateFormats = () => [
        "LT","LTS","L","l","LL",
        "ll","LLL","lll","LLLL","llll",
        "dddd DD.MM", "dddd DD.MM.YYYY",
        "dddd Do MMM", "dddd Do MMMM",
        "dddd Do MMM YYYY", "dddd Do MMMM YYYY",
        "ddd DD.MM", "ddd DD.MM.YYYY",
        "ddd Do MMM", "ddd Do MMMM",
        "ddd Do MMM YYYY", "ddd Do MMMM YYYY",
        "midnight_hack",
    ];

    static createWidget(properties) {
        switch (properties.type) {
            case EmailLayout.IMAGE:
                switch (properties.subType) {
                    case EmailLayout.CUSTOM:
                        return new ImageWidget(properties);
                    case EmailLayout.LIBRARY_IMAGE:
                        return new LibraryImageWidget(properties);
                    case EmailLayout.ENTRY_QRCODE:
                        return new EntryQrWidget(properties);
                    default:
                        break;
                }
                break;
            case EmailLayout.TEXT:
                switch (properties.subType) {
                    case EmailLayout.CUSTOM:
                        return new CustomTextWidget(properties);
                    default:
                        break;
                }
                break;
            default:
                break;
        }

        return null;
    }

    static getAvailableWidgets(type) {
        const availableWidgets = {
            [EmailLayout.IMAGE]: [
                new LibraryImageWidget(),
                new EntryQrWidget(),
                new ImageWidget(),
            ],
            [EmailLayout.TEXT]: [
                new CustomTextWidget(),
            ]
        };

        if (type)
            return availableWidgets[type];

        return availableWidgets;
    }

    static defaultLayout() {
        const logoWidget = new LibraryImageWidget({ }).toPlainObject();
        logoWidget.options.type = 'logo';

        const contentWidget = new CustomTextWidget({ }).toPlainObject();
        Utils.langs().forEach(lang => {
            contentWidget.options.text[lang] = '<p style="text-align: center">...<br />...<br />...<br /></p>';
        });

        const addressWidget = new CustomTextWidget({ }).toPlainObject();
        Utils.langs().forEach(lang => {
            addressWidget.options.text[lang] = '<p style="text-align: center">';
            addressWidget.options.text[lang] += `   [customer.name.${lang}]<br/>`;
            addressWidget.options.text[lang] += `   [customer.address.${lang}]<br/>`;
            addressWidget.options.text[lang] += `   Tél. [customer.phone.${lang}]<br/>`;
            addressWidget.options.text[lang] += `   [customer.email.mail.${lang}]<br/>`;
            addressWidget.options.text[lang] += '</p>';
        });

        const labels = {
            fr: 'Modèle d\'email',
            en: 'Email Layout',
            de: 'Email Layout',
        };
        const lang = Utils.default_lang();

        return {
            _id: v4(),
            layout_type: 'email',
            type: EmailLayout.LAYOUT_TYPE_CAMPAIGN,
            name: Utils.new_pricing_names(labels[lang] || labels.en || 'Email Layout', lang),
            widgets: [
                logoWidget,
                contentWidget,
                addressWidget
            ]
        };
    }

    constructor(properties) {
        super(properties);

        this.widgets = (this.widgets || []).map(w => new EmailLayoutWidget(w));
    }

    /**
     * Prepare this object for update.
     * This is used to "normalize", if needed, some properties
     * before to send them.
     *
     * return{BaseModel}
     */
    prepareForUpdate() {
        const prepared = super.prepareForUpdate();

        if (prepared.options) {
            if (!prepared.options.bcc?.length)
                delete prepared.options.bcc;
            if (!prepared.options.replyTo?.length)
                delete prepared.options.replyTo;
        }

        Utils.cleanTranslatableProperty(prepared, 'subject');

        (prepared.widgets || []).forEach((widget) => {
            if (!widget.options)
                return;
            Utils.cleanTranslatableProperty(widget.options, 'text');
            Utils.cleanTranslatableProperty(widget.options, 'link');
        });

        return prepared;
    }

    /**
     * Prepare this object for duplicate.
     * This is used to "normalize", if needed, some properties
     * before to send them.
     *
     * return{BaseModel}
     */
    prepareForDuplicate() {
        const prepared = _.cloneDeep(this);
        prepared._id = v4();

        return prepared;
    }

    /**
     * Check if a layout has any shortcode of a specific type
     *
     * @param {String} type
     * @param {String} lang
     * @return {Boolean}
     */
    hasShortcodeOfType(type, lang) {
        const escapedType = escapeShortcode(type);
        const regexp = new RegExp(`\\[${escapedType}[.\\]]`, 'g');

        return this.widgets?.some(widget => {
            if (widget.type !== EmailLayout.TEXT)
                return false;

            const texts = widget.options?.text;
            if (!texts || typeof texts !== 'object')
                return false;

            // After pruning stale langs, UI lang may be absent; fall back like display.
            const text = Utils.localized_or_fallback(texts, lang, '');
            return regexp.test(text);
        }) || false;
    }

    /**
     * Check if this email layout contains a widget with
     * the custom text shortcode
     *
     * @param {String} lang
     * @return {Boolean}
     */
    canHaveCustomText(lang) {
        const shortcode = `${EmailLayout.CUSTOM_TEXT_SHORTCODE}.${lang}`;
        return this.hasShortcodeOfType(shortcode, lang)
    }

    /**
     * Convert this widget instance to a plain object
     */
    toPlainObject = () => JSON.parse(JSON.stringify(this));
}

/**
 * Get the fields to get server side when we
 * ask for a listing
 */
EmailLayout.getListingFields = () => ['_id', 'name', 'layout_type', 'type'];

export default EmailLayout;
